import _ from 'lodash';
import clsx from 'clsx';
import Style from './panelStyles.module.scss';
import { useCallback } from 'react';
import LogicPanelStyle from './logicPanelStyles.module.scss';
import { useEditor, useNode } from '@craftjs/core';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Edit } from 'assets/img/generalBuilder/edit_24.svg';
import { logger } from 'src/application/logging/logging';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { getNodeByDataKeyFunc, hasSection } from './helpers/helper';
import { useConditionalCreator } from './helpers/useConditional';
import { ExpressionNodeType, ExpressionType } from './helpers/expression/types';
import { ConditionalProps, isFieldWithOptions } from '../../types/BaseControlTypes';
import Expression from './helpers/expression/expression';
import { Chip } from './uiKit/chip/chip';
import ExpressionBuilder from './helpers/expression/expressionBuilder';
import { PanelContainer } from './uiKit/panelContainer/panelContainer';
import { ControlsGroup } from './uiKit/controlsGroup/controlsGroup';
import { AppButton } from 'uikit/buttons';
import { useSelectNode } from './hooks/useSelectNode';
import { getTimeInputOptions } from 'src/features/generalForms/uikit/fields/time/timePickerOptions';
import { getConditionalInvalidValuesError } from './helpers/conditionalInvalidValuesError';
import { AppInputError } from 'uikit/wrappers/error/appInputError.component';

type LogicPanelSection = 'Enable' | 'Visible';

export type LogicPanelProps = {
  sections: LogicPanelSection[];
};

export const LogicPanel = ({ sections }: LogicPanelProps) => {
  const { t: commonTrans } = useTranslation();
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.Logic');
  const {
    getNodeByDataKey,
    connectors: { hover },
  } = useEditor((state) => ({
    getNodeByDataKey: getNodeByDataKeyFunc(state),
  }));
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as ConditionalProps,
  }));

  const selectNode = useSelectNode();

  const enableIfModal = useConditionalCreator('enableIf');
  const visibleIfModal = useConditionalCreator('visibleIf');

  const renderNodeValue = useCallback(
    (node: ExpressionNodeType) => {
      const fieldProps = getNodeByDataKey(node.dataKey)?.data.props;
      const fieldResolvedName = getNodeByDataKey(node.dataKey)?.data.name;

      if (fieldResolvedName === 'FormTimeInput') {
        const timeOptions = getTimeInputOptions(fieldProps?.timeIntervalInMinutes);
        const timeOptionsMap = new Map(timeOptions.map((option) => [option.key, option.text]));

        if (_.isArray(node.value)) {
          return node.value
            .map((value) => Number(value))
            .filter((value) => !Number.isNaN(value) && timeOptionsMap.has(value))
            .map((value) => timeOptionsMap.get(value))
            .join(', ');
        }

        const timeValue = Number(node.value);

        if (Number.isNaN(timeValue)) {
          return node.value;
        }

        return timeOptionsMap.get(timeValue);
      }

      if (isFieldWithOptions(fieldProps)) {
        return fieldProps.options
          .filter((x) => node.value?.includes(x.key.toString()))
          ?.map((o) => o.value)
          .join(', ');
      }

      return node.value;
    },
    [getNodeByDataKey],
  );

  const renderExpressionNode = useCallback(
    (n: ExpressionType): React.JSX.Element => {
      if (Expression.isExpressionNode(n)) {
        const invalidOptionError = getConditionalInvalidValuesError(
          n,
          getNodeByDataKey,
          (values) => `${t('Errors.MissingValues', { values })}`,
        );

        return (
          <AppInputError hideBorder={true} errors={invalidOptionError}>
            <div
              className={LogicPanelStyle.conditionItem}
              ref={(ref) => {
                const node = getNodeByDataKey(n.dataKey);
                if (ref && node) hover(ref, node.id);
              }}
              onClick={() => {
                selectNode(getNodeByDataKey(n.dataKey)!.id);
              }}
            >
              <div className={LogicPanelStyle.questionTitle}>
                {getNodeByDataKey(n.dataKey)?.data.props.title ??
                  commonTrans(
                    `GeneralFormBuilder.Panels.Controls.${getNodeByDataKey(n.dataKey)?.data.displayName}`,
                    `DataKey: ${n.dataKey}`,
                  )}
              </div>
              <div className={LogicPanelStyle.truncate}>
                <span className={LogicPanelStyle.comparisonOperator}>{t(`ComparisonOperator.${n.operator}`)}</span>
                {!_.isNil(n.value) && <span>{renderNodeValue(n)}</span>}
              </div>
            </div>
          </AppInputError>
        );
      }

      return (
        <>
          {renderExpressionNode(n.operandOne)}
          <Chip text={t(`LogicalOperator.${n.operator}`)} colorSchema={'yellow'} />
          {renderExpressionNode(n.operandTwo)}
        </>
      );
    },
    [commonTrans, getNodeByDataKey, hover, renderNodeValue, selectNode, t],
  );

  const renderExpression = useCallback(
    (expString: string | undefined) => {
      if (!expString) {
        return <></>;
      }

      let expression;
      try {
        expression = ExpressionBuilder.fromString(expString).expression;
      } catch (error) {
        logger().error('Wrong expression string.', error);
      }

      if (!expression) {
        return <></>;
      }

      return <div className={LogicPanelStyle.conditionsContainer}>{renderExpressionNode(expression)}</div>;
    },
    [renderExpressionNode],
  );

  return (
    <>
      <PanelContainer header={t('Header')} panelName={'Logic'}>
        {hasSection<LogicPanelSection>(sections, 'Visible') && (
          <ControlsGroup
            header={t('VisibleIf')}
            contentClassName={clsx(Style.flexContainer, LogicPanelStyle.gap8)}
            collapsible={true}
            ignoreInnerExpandedState={false}
            expanded={!!props.visibleIf}
            onClose={() => {
              setProp((prop: ConditionalProps) => {
                prop.visibleIf = undefined;
              }, 500);
            }}
            onOpen={async () => {
              const result = await visibleIfModal.start();
              if (!result) return Promise.reject('Rejected');

              setProp((prop: ConditionalProps) => {
                prop.visibleIf = result;
              }, 500);
            }}
          >
            <AppButton
              variant={'icon-link'}
              text={commonTrans('Common_Edit')}
              colorSchema={'secondary'}
              Icon={Edit}
              onClick={async () => {
                const result = await visibleIfModal.start(props.visibleIf);
                if (!result) return;

                setProp((prop: ConditionalProps) => {
                  prop.visibleIf = result;
                }, 500);
              }}
            />
            {renderExpression(props.visibleIf)}
          </ControlsGroup>
        )}

        {hasSection<LogicPanelSection>(sections, 'Enable') && (
          <ControlsGroup
            header={t('EnableIf')}
            contentClassName={clsx(Style.flexContainer, LogicPanelStyle.gap8)}
            collapsible={true}
            ignoreInnerExpandedState={false}
            expanded={!!props.enableIf}
            onClose={async () => {
              setProp((prop: ConditionalProps) => {
                prop.enableIf = undefined;
              }, 500);
            }}
            onOpen={async () => {
              const result = await enableIfModal.start();
              if (!result) return Promise.reject('Rejected');

              setProp((prop: ConditionalProps) => {
                prop.enableIf = result;
              }, 500);
            }}
          >
            <AppButton
              variant={'icon-link'}
              text={commonTrans('Common_Edit')}
              colorSchema={'secondary'}
              Icon={Edit}
              onClick={async () => {
                const result = await enableIfModal.start(props.enableIf);
                if (!result) return;

                setProp((prop: ConditionalProps) => {
                  prop.enableIf = result;
                }, 500);
              }}
            />
            {renderExpression(props.enableIf)}
          </ControlsGroup>
        )}
      </PanelContainer>
      {enableIfModal.modal}
      {visibleIfModal.modal}
    </>
  );
};
